import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HistoryImportProgressEvent, HistoryImportRun } from '@troia-v3/types';

import { useSocket } from '@/contexts/SocketContext';
import { historyImportKeys } from './useHistoryImport';

/** Payload do evento `history-import:qr` (webhook `qr` do worker repassado pelo backend). */
export interface HistoryImportQrEventPayload {
  runId: string;
  channelId: string;
  qrCode: string;
  expires: number;
}

export interface HistoryImportSocketHandlers {
  onQr?: (data: HistoryImportQrEventPayload) => void;
  onStatus?: (data: HistoryImportProgressEvent) => void;
  onProgress?: (data: HistoryImportProgressEvent) => void;
  onCompleted?: (data: HistoryImportProgressEvent) => void;
  onFailed?: (data: HistoryImportProgressEvent) => void;
}

/**
 * Assina os eventos de socket de uma run de importação de histórico
 * (`history-import:qr|status|progress|completed|failed`), filtrando por
 * `runId`. String literal nos nomes dos eventos — mesma convenção de
 * `useChannelSyncSocket.ts` (bug conhecido de pre-bundle do Vite com
 * `SOCKET_EVENTS` importado de `@troia-v3/types`).
 *
 * `status`/`progress`/`completed`/`failed` fazem merge de `status`+`counters`
 * direto no cache do `useImportRun` (mesma query key) — a UI reflete a run
 * instantaneamente, sem esperar o próximo poll de 5s. Em `completed`, invalida
 * `['conversations']` (mensagens importadas viram conversas). Em `failed`,
 * invalida a própria run (o evento de socket não carrega `run.error` — só
 * status/counters — a run completa via REST tem o motivo).
 *
 * @example
 * useHistoryImportSocket(runId, {
 *   onQr: (data) => setQr({ qrCode: data.qrCode, expires: data.expires }),
 * });
 */
export const useHistoryImportSocket = (
  runId: string | undefined,
  handlers: HistoryImportSocketHandlers = {},
): void => {
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !isConnected || !runId) return;

    const mergeRunCache = (data: HistoryImportProgressEvent): void => {
      queryClient.setQueryData<HistoryImportRun>(historyImportKeys.run(runId), (old) =>
        old ? { ...old, status: data.status, counters: data.counters } : old,
      );
    };

    const onQr = (data: HistoryImportQrEventPayload): void => {
      if (!data || data.runId !== runId) return;
      handlers.onQr?.(data);
    };

    const onStatus = (data: HistoryImportProgressEvent): void => {
      if (!data || data.runId !== runId) return;
      mergeRunCache(data);
      handlers.onStatus?.(data);
    };

    const onProgress = (data: HistoryImportProgressEvent): void => {
      if (!data || data.runId !== runId) return;
      mergeRunCache(data);
      handlers.onProgress?.(data);
    };

    const onCompleted = (data: HistoryImportProgressEvent): void => {
      if (!data || data.runId !== runId) return;
      mergeRunCache(data);
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      handlers.onCompleted?.(data);
    };

    const onFailed = (data: HistoryImportProgressEvent): void => {
      if (!data || data.runId !== runId) return;
      mergeRunCache(data);
      void queryClient.invalidateQueries({ queryKey: historyImportKeys.run(runId) });
      handlers.onFailed?.(data);
    };

    socket.on('history-import:qr', onQr);
    socket.on('history-import:status', onStatus);
    socket.on('history-import:progress', onProgress);
    socket.on('history-import:completed', onCompleted);
    socket.on('history-import:failed', onFailed);

    return () => {
      socket.off('history-import:qr', onQr);
      socket.off('history-import:status', onStatus);
      socket.off('history-import:progress', onProgress);
      socket.off('history-import:completed', onCompleted);
      socket.off('history-import:failed', onFailed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, runId]);
};
